import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

/**
 * Vessel name → IMO / MMSI, inline on the shipping form.
 *
 * Logistics only ever gets a vessel NAME off the carrier's booking
 * confirmation, but AIS tracking subscribes by MMSI — so without this hop the
 * live tracking board stays empty no matter how carefully the form is filled.
 *
 * It never auto-picks. Vessel names are genuinely ambiguous (ships get renamed
 * and names get reused, which is the whole reason IMO numbers exist), so
 * candidates are listed and the operator confirms. When nothing matches, the
 * fallback is to type the IMO/MMSI straight off the booking confirmation —
 * that gets saved to the registry so the next voyage resolves by itself.
 */

export type VesselCandidate = {
  name: string;
  imo: string;
  mmsi: string;
  callSign: string;
  vesselType: string;
  flag: string;
  source: 'wikidata' | 'ais' | 'manual';
  lastSeenAt: string | null;
  matchedOn: string;
  trackable: boolean;
};

const SOURCE_LABEL: Record<VesselCandidate['source'], string> = {
  wikidata: 'Registry',
  ais: 'Seen on AIS',
  manual: 'Entered by us',
};

export default function VesselResolver({
  vesselName, imo, mmsi, onPick,
}: {
  vesselName: string;
  imo: string;
  mmsi: string;
  onPick: (v: { imo: string; mmsi: string; name?: string }) => void;
}) {
  const [candidates, setCandidates] = useState<VesselCandidate[]>([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState('');
  const [manual, setManual] = useState(false);
  const [mImo, setMImo] = useState('');
  const [mMmsi, setMMmsi] = useState('');
  const [err, setErr] = useState('');

  // Debounced so a lookup fires per pause, not per keystroke.
  useEffect(() => {
    const q = vesselName.trim();
    if (q.length < 3) { setCandidates([]); setSearched(''); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get<{ candidates: VesselCandidate[]; normalised: string }>(
          `/api/procurement/vessel-tracking/lookup?q=${encodeURIComponent(q)}`);
        setCandidates(r.candidates || []);
        setSearched(r.normalised || q);
      } catch { setCandidates([]); }
      finally { setBusy(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [vesselName]);

  async function saveManual() {
    setErr('');
    try {
      const r = await api.post<{ ok: boolean; message?: string; trackable: boolean }>(
        '/api/procurement/vessel-tracking/registry',
        { name: vesselName.trim(), imo: mImo.trim(), mmsi: mMmsi.trim() });
      if (!r.ok) { setErr(r.message || 'Could not save'); return; }
      onPick({ imo: mImo.trim(), mmsi: mMmsi.trim() });
      setManual(false);
    } catch (e: any) {
      setErr(e?.message || 'Could not save');
    }
  }

  if (vesselName.trim().length < 3) return null;

  const resolved = !!mmsi || !!imo;

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-[11.5px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-700">Vessel identity</span>
        {busy && <span className="text-slate-400">searching…</span>}
        {resolved && (
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-800">
            {mmsi ? 'Live tracking will work' : 'IMO only — needs MMSI to track'}
          </span>
        )}
        {searched && searched !== vesselName.trim().toUpperCase() && (
          <span className="text-[10.5px] text-slate-400">searched as “{searched}”</span>
        )}
        <button
          type="button"
          className="ml-auto text-[11px] font-semibold text-blue-700 underline-offset-2 hover:underline"
          onClick={() => { setManual((v) => !v); setMImo(imo); setMMmsi(mmsi); }}
        >
          {manual ? 'Cancel' : 'Enter manually'}
        </button>
      </div>

      {resolved && (
        <div className="mt-1.5 font-mono text-[11px] text-slate-600">
          IMO {imo || '—'} · MMSI {mmsi || '—'}
        </div>
      )}

      {manual && (
        <div className="mt-2 space-y-2 rounded border border-slate-200 bg-white p-2">
          <p className="text-[10.5px] text-slate-500">
            Both are on the carrier's booking confirmation. MMSI (9 digits) is what drives live
            tracking; IMO (7 digits) is the permanent hull id. Saved to the registry so this
            vessel resolves on its own next time.
          </p>
          <div className="flex flex-wrap gap-2">
            <input className="input flex-1 font-mono" placeholder="IMO (7 digits)" value={mImo}
              onChange={(e) => setMImo(e.target.value.replace(/\D/g, '').slice(0, 7))} />
            <input className="input flex-1 font-mono" placeholder="MMSI (9 digits)" value={mMmsi}
              onChange={(e) => setMMmsi(e.target.value.replace(/\D/g, '').slice(0, 9))} />
            <button type="button" className="pc-btn pc-btn-primary" onClick={saveManual}>Save</button>
          </div>
          {err && <div className="text-[11px] font-semibold text-rose-700">{err}</div>}
        </div>
      )}

      {!manual && candidates.length > 0 && (
        <ul className="mt-2 max-h-44 space-y-1 overflow-y-auto">
          {candidates.map((c, i) => {
            const picked = (!!c.imo && c.imo === imo) || (!!c.mmsi && c.mmsi === mmsi);
            return (
              <li key={`${c.imo || c.mmsi || c.name}-${i}`}>
                <button
                  type="button"
                  onClick={() => onPick({ imo: c.imo, mmsi: c.mmsi, name: c.name })}
                  className={
                    'flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 rounded border px-2 py-1.5 text-left transition ' +
                    (picked ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50')
                  }
                >
                  <span className="font-semibold text-slate-800">{c.name}</span>
                  <span className="font-mono text-[10.5px] text-slate-500">
                    IMO {c.imo || '—'} · MMSI {c.mmsi || '—'}
                  </span>
                  {!c.trackable && (
                    <span className="rounded bg-amber-100 px-1 py-0.5 text-[9.5px] font-semibold text-amber-800">
                      no MMSI
                    </span>
                  )}
                  <span className="ml-auto text-[9.5px] uppercase tracking-wide text-slate-400">
                    {SOURCE_LABEL[c.source]} · {c.matchedOn}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!manual && !busy && searched && candidates.length === 0 && (
        <p className="mt-1.5 text-[11px] text-slate-500">
          No match in the registry — feeders and coastal vessels are often missing.
          Use <span className="font-semibold">Enter manually</span> with the IMO/MMSI from the booking confirmation.
        </p>
      )}
    </div>
  );
}
